import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Link } from 'react-router-dom';
import { LogOut, Menu, X, Sun, Moon, Volume2, VolumeX, Tv } from 'lucide-react';
import pulseLogo from '../assets/pulse-logo.png';

interface NavbarProps {
  mobileMenuOpen?: boolean;
  onToggleMobileMenu?: () => void;
}

export function Navbar({ mobileMenuOpen, onToggleMobileMenu }: NavbarProps) {
  const { user, logout } = useAuth();
  const { resolvedTheme, toggleTheme, soundEnabled, toggleSound } = useTheme();

  return (
    <header className="h-14 border-b border-black/[0.06] dark:border-white/10 bg-white/80 dark:bg-[#0C0C0C]/85 backdrop-blur-2xl sticky top-0 z-40 px-3 sm:px-6 flex items-center justify-between transition-colors">
      <div className="flex items-center gap-2 sm:gap-6 min-w-0">
        {/* Mobile Hamburger Button */}
        {onToggleMobileMenu && (
          <button
            onClick={onToggleMobileMenu}
            aria-label={mobileMenuOpen ? 'Close Navigation Menu' : 'Open Navigation Menu'}
            className="lg:hidden p-2 -ml-1 text-zinc-500 hover:text-black dark:text-zinc-400 dark:hover:text-white rounded-xl hover:bg-black/5 dark:hover:bg-zinc-800/80 active:bg-zinc-700/50 ios-press flex items-center justify-center"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        )}

        <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
          <img src={pulseLogo} alt="CK Pulse" className="w-8 h-8 object-contain shrink-0 drop-shadow" />
          <span className="font-bold text-zinc-900 dark:text-white tracking-tight text-sm shrink-0">CK Pulse</span>
          <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-zinc-200/80 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border border-black/5 dark:border-white/10 max-w-[110px] sm:max-w-[180px] truncate shrink font-mono">
            {user?.role === 'SUPER_ADMIN' ? 'Super Admin' : user?.organization?.name || 'Workspace'}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
        {user?.role === 'ADMIN' && (
          <Link
            to="/admin/kiosk"
            target="_blank"
            rel="noopener noreferrer"
            title="Launch TV Kiosk / Wallboard Mode"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-zinc-700 dark:text-zinc-200 hover:text-black dark:hover:text-white bg-black/5 dark:bg-white/5 hover:bg-black/10 dark:hover:bg-white/10 border border-black/5 dark:border-white/10 ios-press transition-colors"
          >
            <Tv className="w-3.5 h-3.5 text-blue-500" />
            <span>TV Kiosk</span>
          </Link>
        )}

        {/* Sound toggle button */}
        <button
          onClick={toggleSound}
          title={soundEnabled ? 'Mute Sounds' : 'Enable Sounds'}
          className="p-2 rounded-xl text-zinc-500 hover:text-black dark:text-zinc-400 dark:hover:text-white hover:bg-black/5 dark:hover:bg-zinc-800 border border-transparent hover:border-black/5 dark:hover:border-white/10 ios-press"
        >
          {soundEnabled ? <Volume2 className="w-4 h-4 text-amber-500" /> : <VolumeX className="w-4 h-4 opacity-50" />}
        </button>

        {/* Theme toggle button (Sun / Moon) */}
        <button
          onClick={toggleTheme}
          title={`Switch to ${resolvedTheme === 'dark' ? 'Light' : 'Dark'} Mode`}
          className="p-2 rounded-xl text-zinc-500 hover:text-black dark:text-zinc-400 dark:hover:text-white hover:bg-black/5 dark:hover:bg-zinc-800 border border-transparent hover:border-black/5 dark:hover:border-white/10 ios-press"
        >
          {resolvedTheme === 'dark' ? (
            <Sun className="w-4 h-4 text-amber-400 transition-transform hover:rotate-45" />
          ) : (
            <Moon className="w-4 h-4 text-indigo-600 transition-transform hover:-rotate-12" />
          )}
        </button>

        {user && (
          <div className="flex items-center gap-2 sm:gap-3 pl-1 sm:pl-2 border-l border-black/5 dark:border-white/10">
            <div className="hidden sm:flex flex-col text-right">
              <span className="text-xs font-semibold text-zinc-900 dark:text-white max-w-[140px] truncate">{user.fullName}</span>
              <span className="text-[11px] text-zinc-500 dark:text-zinc-400 max-w-[140px] truncate">{user.email}</span>
            </div>

            <button
              onClick={logout}
              title="Sign Out"
              className="p-2 rounded-xl text-zinc-500 hover:text-rose-500 dark:text-zinc-400 dark:hover:text-rose-400 hover:bg-black/5 dark:hover:bg-zinc-800 border border-transparent hover:border-black/5 dark:hover:border-white/10 ios-press"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
